<template>
  <div class="custom-leaflet-container" style="height:100%; width:100%;">
    <l-map ref="map" :use-global-leaflet="false" :options="mapOptions" @ready="init">
      <div v-if="mapStore.isSpin" class="map-spinner-overlay">
        <div class="map-spinner" />
      </div>
    </l-map>
  </div>
</template>

<script setup lang="ts">
import 'leaflet/dist/leaflet.css'
import 'leaflet.fullscreen/Control.FullScreen.css'
import 'leaflet.fullscreen'
import { LMap/*, LControl */ } from '@vue-leaflet/vue-leaflet'
import type MapMarkerEventData from '~/types/search/MapMarkerEventData'
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'
import useSearchResultsMapCore from '~/composables/search/useSearchResultsMapCore'
import { BREAKPOINT_LG_PX } from '~/utils/breakpoints'

const props = defineProps({
  data: {
    type: Object,
    default: null
  },
  pending: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['markerMouseOver', 'markerMouseOut', 'markerClick'])
const markerEventsHandler = (eventName: string, data: MapMarkerEventData): void => {
  emit(eventName, { data })
}

const mapStore = useSearchResultsMapStore()

const map = ref<typeof LMap>()

// Desktop: moveend is active immediately
const isMoveendActive = ref(true)

const { mapOptions, initMap, cleanup } = useSearchResultsMapCore(
  map,
  props,
  markerEventsHandler,
  isMoveendActive
)

let initCalled = false
const fullscreenControl = new L.Control.FullScreen({ position: 'topright' })

const init = () => {
  // Early return: Map or Leaflet object is missing
  if (!map.value?.leafletObject) {
    return
  }

  // Prevent multiple initializations during resize/tab-switch: only adjust map size
  if (initCalled) {
    map.value.leafletObject.invalidateSize()
    return
  }

  const mapContainer = map.value.$el.closest('#map-search')

  if (!(mapContainer?.offsetWidth > 0 || mapContainer?.offsetHeight > 0)) {
    return
  }

  if (mapStore.isAppleDevice) {
    fullscreenControl.addTo(map.value.leafletObject)
    fullscreenControl.getContainer().addEventListener('mousedown', () => {
      if (fullscreenControl.toggleFullScreen && window.innerWidth < BREAKPOINT_LG_PX) {
        mapStore.fullSize = false
        setTimeout(() => {
          mapStore.visible = false
        }, 300)
      }
    })
  }

  initMap(mapContainer)
  nextTick(() => { handleScroll() })
  initCalled = true
}

watch(() => mapStore.fullSize, (newValue) => {
  init()
  mapStore.search = false
  mapStore.moveMapSearch = false
  fullscreenControl.toggleFullScreen()
  if (newValue === true) {
    mapStore.search = false
    mapStore.moveMapSearch = false
    mapStore.moveMapIteration = 0
    window.history.pushState({ fullscreenMapOpen: true, hash: '' }, '')
    // Reset search flag after fullscreen animation settles
    setTimeout(() => {
      mapStore.search = true
    }, 500)
  }
})

const handleFullscreenChange = () => {
  if (!document.fullscreenElement && window.innerWidth < BREAKPOINT_LG_PX) {
    mapStore.fullSize = false
    setTimeout(() => {
      mapStore.visible = false
    }, 300)
  }
}

const handlePopState = () => {
  if (document.fullscreenElement && window.innerWidth < BREAKPOINT_LG_PX) {
    if (mapStore.moveMapIteration === 0) {
      mapStore.fullSize = false
      setTimeout(() => {
        mapStore.visible = false
      }, 300)
    }
  }
}

const handleResize = () => {
  mapStore.preservePageOnNextLimitsChange = true
  if (map.value?.leafletObject) {
    map.value.leafletObject.invalidateSize()
  }
  mapStore.moveMapSearch = false
}

const handleScroll = () => {
  const mapElement = map.value?.$el
  if (!mapElement) { return }

  const scrollPosition = window.scrollY - 400
  const mapTop = mapElement.getBoundingClientRect().top

  if (scrollPosition >= mapTop) {
    mapElement.style.position = 'relative'
    mapElement.style.top = '0'
    mapElement.style.height = '100%'
    mapElement.style.width = '100%'
  } else {
    mapElement.style.position = 'relative'
    mapElement.style.height = window.innerHeight - mapTop + 'px'
  }
}

onMounted(() => {
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  window.addEventListener('popstate', handlePopState)
  window.addEventListener('scroll', handleScroll)
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  cleanup()

  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('popstate', handlePopState)
})

onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('popstate', handlePopState)
})
</script>
<style src="./css/SearchResultMap.css"></style>
